import React, { useState } from 'react';
import { Table, Button, Badge } from 'react-bootstrap';
import {
    DndContext,
    closestCenter
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const DragHandle = ({ listeners, attributes }) => (
    <span
        {...listeners}
        {...attributes}
        style={{ cursor: 'grab', marginRight: 8, color: '#888', fontSize: '1.2em', userSelect: 'none' }}
        title="Arrastrar para reordenar"
    >
        ☰
    </span>
);

const LecturasTable = ({
    lecturas = [],
    handleEdit = () => {},
    handleDelete = () => {},
    handleOnDragEnd = () => {},
}) => {
    const [activeTab, setActiveTab] = useState('1');

    // Organizar lecturas por nivel
    const niveles = lecturas.reduce((acc, lectura) => {
        acc[lectura.level] = acc[lectura.level] || [];
        acc[lectura.level].push(lectura);
        return acc;
    }, {});

    // Componente SortableRow para usar con dnd-kit
    function SortableRow({ id, lectura, children }) {
        const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
            isDragging
        } = useSortable({ id });
        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            background: isDragging ? '#f4f4f4' : undefined
        };
        // children: [posicionTd, accionesTd]
        return (
            <tr ref={setNodeRef} style={style} className={`sortable-row ${isDragging ? 'dragging' : ''}`}>
                {/* Nombre con handle de drag */}
                <td className="fw-semibold d-flex align-items-center">
                    <DragHandle listeners={listeners} attributes={attributes} />
                    <span className="me-2">📚</span>
                    {lectura.nombre}
                </td>
                {/* Posición */}
                {children[0]}
                {/* Acciones */}
                {children[1]}
            </tr>
        );
    }

    // Nueva función de drag end para dnd-kit
    const handleDragEnd = (event, nivel) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const items = niveles[nivel] || [];
        const oldIndex = items.findIndex(l => l.id === active.id);
        const newIndex = items.findIndex(l => l.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        const reordered = arrayMove(items, oldIndex, newIndex);
        // Actualizar posiciones
        const updatedItems = reordered.map((item, idx) => ({ ...item, posicion: idx + 1 }));
        handleOnDragEnd({ updatedItems, nivel });
    };

    const getNivelColor = (nivel) => {
        const colors = {
            '1': 'primary',
            '2': 'success',
            '3': 'warning'
        };
        return colors[nivel] || 'secondary';
    };

    return (
        <div className="lecturas-table animate-fade-in">
            <ul className="nav nav-tabs justify-content-center mb-4" role="tablist">
                {Object.keys(niveles).sort().map((nivel) => (
                    <li key={nivel} className="nav-item">
                        <button
                            className={`nav-link ${activeTab === nivel ? 'active' : ''}`}
                            onClick={() => setActiveTab(nivel)}
                            role="tab"
                            aria-selected={activeTab === nivel}
                            id={`nivel-${nivel}-tab`}
                        >
                            Nivel {nivel}
                            <Badge bg={getNivelColor(nivel)} className="ms-2">
                                {niveles[nivel].length}
                            </Badge>
                        </button>
                    </li>
                ))}
            </ul>
            <div className="tab-content">
                {Object.keys(niveles).sort().map((nivel) => (
                    <div
                        key={nivel}
                        className={`tab-pane fade ${activeTab === nivel ? 'show active' : ''}`}
                        id={`nivel-${nivel}`}
                        role="tabpanel"
                        aria-labelledby={`nivel-${nivel}-tab`}
                    >
                        <DndContext collisionDetection={closestCenter} onDragEnd={event => handleDragEnd(event, nivel)}>
                            <SortableContext
                                items={niveles[nivel]?.map(l => l.id) || []}
                                strategy={verticalListSortingStrategy}
                            >
                                <Table bordered hover responsive className="mb-0 table-suave">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '60%' }} className="text-uppercase text-primary">Nombre</th>
                                            <th style={{ width: '20%' }} className="text-uppercase text-primary">Posición</th>
                                            <th style={{ width: '20%' }} className="text-uppercase text-primary">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(niveles[nivel] || [])
                                            .sort((a, b) => a.posicion - b.posicion)
                                            .map((lectura) => (
                                                <SortableRow key={lectura.id} id={lectura.id} lectura={lectura}>
                                                    {/* Posición */}
                                                    <td>
                                                        <Badge bg="info" className="px-3 py-2">
                                                            {lectura.posicion}
                                                        </Badge>
                                                    </td>
                                                    {/* Acciones */}
                                                    <td>
                                                        <div className="lecturas-actions d-flex gap-2 justify-content-end">
                                                            <Button
                                                                variant="info"
                                                                onClick={() => handleEdit(lectura)}
                                                                size="sm"
                                                                className="d-flex align-items-center"
                                                            >
                                                                <span className="me-1">✏️</span>
                                                                Editar
                                                            </Button>
                                                            <Button
                                                                variant="danger"
                                                                onClick={() => handleDelete(lectura.id)}
                                                                size="sm"
                                                                className="d-flex align-items-center"
                                                            >
                                                                <span className="me-1">🗑️</span>
                                                                Eliminar
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </SortableRow>
                                            ))}
                                    </tbody>
                                </Table>
                            </SortableContext>
                        </DndContext>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default LecturasTable;
